import { Schema } from "effect"
import { CommandRejection, CommandResult } from "./commands/index"
import { Evidence, Risk } from "./common"
import { ArtifactKind, Blocker } from "./domain/task"
import { ArtifactId, CommentId, FrameSeq, Millis, PosInt, ProposalId, Slug } from "./ids"
import { RunOutcome, Verdict } from "./run"

// #region graph-tools
/**
 * The graph tools of the integration wave (decision 0011, §6.9 of the wave spec). The Project DO serves them over
 * MCP (`POST /api/runs/:runId/mcp`, runner minds) and in process (`ProjectDO.callTool`, platform minds). Every call
 * becomes a CommandEnvelope and is re-checked by `decide`.
 */

/** A label an agent defines in a plan (`$offline-toggle`) and references elsewhere in the same plan. */
export const PlanLabel = Schema.String.check(Schema.isPattern(/^\$[a-z0-9][a-z0-9-]{0,39}$/))
/** Where an id goes, an agent may write a real id, a short ref ("T-12") or a $label. Resolved by the tool layer. */
export const ToolRef = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64))

/** close_task evidence: protocol Evidence, or sugar the tool layer resolves (`{run: "R-24"}`, `{artifact: "<id>"}`). */
export const CloseEvidence = Schema.Union([
  Evidence,
  Schema.Struct({ run: ToolRef }),
  Schema.Struct({ artifact: ToolRef }),
])

/**
 * Tool inputs. `plan`/`propose_plan` changes are GraphCommands as JSON with refs and labels in id positions; the tool
 * layer resolves them, fills defaults, then decodes each with GraphCommand. Evidence in report_outcome and
 * report_verdict: the tool layer fills an omitted `check.detail` with null before decoding (A19).
 */
export const GraphToolInputs = {
  read_context: Schema.Struct({}),
  read_graph: Schema.Struct({
    include: Schema.optionalKey(Schema.Array(Schema.Literals(["done", "cancelled", "archived"]))),
  }),
  plan: Schema.Struct({
    rationale: Schema.NonEmptyString,
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
    mode: Schema.optionalKey(Schema.Literals(["apply_allowed", "propose_all"])),
  }),
  propose_plan: Schema.Struct({
    rationale: Schema.NonEmptyString,
    oneLine: Schema.String.check(Schema.isMaxLength(140)),
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
    risk: Schema.optionalKey(Risk),
    decideBy: Schema.optionalKey(Millis),
  }),
  ask_human: Schema.Struct({
    prompt: Schema.NonEmptyString,
    options: Schema.Array(
      Schema.Struct({
        key: Slug,
        label: Schema.String,
        detail: Schema.optionalKey(Schema.String),
        /** Commands that run as the person who picks this option (OptionEffect JSON with refs, like `plan` changes). */
        effects: Schema.optionalKey(Schema.Array(Schema.Unknown).check(Schema.isMaxLength(10))),
      }),
    ).check(Schema.isMaxLength(8)),
    allowFreeText: Schema.optionalKey(Schema.Boolean), // default true
    subject: Schema.optionalKey(ToolRef),
    blocks: Schema.optionalKey(Schema.Array(ToolRef)),
    decideBy: Schema.optionalKey(Millis),
  }),
  comment: Schema.Struct({
    target: ToolRef,
    body: Schema.NonEmptyString,
    threadId: Schema.optionalKey(CommentId),
  }),
  ask: Schema.Struct({ target: ToolRef, body: Schema.NonEmptyString }),
  answer: Schema.Struct({ askId: CommentId, body: Schema.NonEmptyString }),
  report_outcome: Schema.Struct({ outcome: RunOutcome }),
  report_blocker: Schema.Struct({ summary: Schema.String, blocker: Blocker }),
  report_verdict: Schema.Struct({ verdict: Verdict }),
  post_artifact: Schema.Struct({
    kind: ArtifactKind,
    title: Schema.String,
    uri: Schema.String,
    contentType: Schema.optionalKey(Schema.String),
    preview: Schema.optionalKey(Schema.String),
    task: Schema.optionalKey(ToolRef),
  }),
  review: Schema.Struct({
    task: ToolRef,
    verdict: Schema.Literals(["approve", "changes_requested"]),
    notes: Schema.String,
    findings: Schema.optionalKey(Schema.Array(Schema.String)),
  }),
  retry_task: Schema.Struct({ task: ToolRef, note: Schema.optionalKey(Schema.String) }),
  unblock_task: Schema.Struct({
    task: ToolRef,
    note: Schema.NonEmptyString,
    hold: Schema.optionalKey(Schema.Boolean),
  }),
  // ---- fix wave 2026-10-09 §4.6 (decision 0021) ----
  /** "P-3" or an id: the full card, including its answer. */
  read_proposal: Schema.Struct({ proposal: ToolRef }),
  /** "T-1" or an id: the task with its runs, blocker, review, thread and proposals. */
  read_task: Schema.Struct({ task: ToolRef }),
  /** "R-9" or an id: the run's result and (optionally) the tail of its transcript, scrubbed of secrets. */
  read_run: Schema.Struct({
    run: ToolRef,
    transcript: Schema.optionalKey(
      Schema.Struct({
        /** Messages from the end; default 10. */
        last: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 50 }))),
        /** Paging: messages before this frameSeq (`nextBefore` of the previous page). */
        before: Schema.optionalKey(FrameSeq),
      }),
    ),
  }),
  /** The workspace's runners as this project's principal sees them: status, profiles, access modes, MCP names. */
  list_runners: Schema.Struct({}),
  stop_run: Schema.Struct({ run: ToolRef, reason: Schema.NonEmptyString }),
  withdraw_proposal: Schema.Struct({ proposal: ToolRef, note: Schema.optionalKey(Schema.String) }),
  // ---- lead control wave (decision 0032) ----
  close_task: Schema.Struct({
    task: ToolRef,
    as: Schema.Literals(["done", "cancelled", "impossible"]),
    note: Schema.NonEmptyString,
    /** Required for `done` (checked by the handler); ignored otherwise. */
    evidence: Schema.optionalKey(Schema.Array(CloseEvidence).check(Schema.isMaxLength(20))),
  }),
  pause_task: Schema.Struct({
    task: ToolRef,
    paused: Schema.Boolean,
    note: Schema.optionalKey(Schema.String),
  }),
  revise_proposal: Schema.Struct({
    proposal: ToolRef,
    rationale: Schema.NonEmptyString,
    changes: Schema.Array(Schema.Unknown).check(Schema.isMinLength(1), Schema.isMaxLength(50)),
  }),
} as const
export type GraphToolName = keyof typeof GraphToolInputs
export const GraphToolNames = Object.keys(GraphToolInputs) as ReadonlyArray<GraphToolName>

/** Every graph tool answers with this JSON (MCP: structuredContent + the same JSON as one text block). */
export const GraphToolOutput = Schema.Struct({
  ok: Schema.Boolean,
  /** Commands. */
  result: Schema.optionalKey(CommandResult),
  /** ok = false (MCP isError = true). */
  rejection: Schema.optionalKey(CommandRejection),
  /** How to fix and retry: one line. */
  hint: Schema.optionalKey(Schema.String),
  /** The raw schema error next to the one-line hint (≤ 800 chars). */
  detail: Schema.optionalKey(Schema.String),
  /** Things worth knowing that did not stop the call (plan: no repo, runner offline, profile not ready). */
  warnings: Schema.optionalKey(Schema.Array(Schema.String)),
  /** Set whenever a proposal was opened (chat cards key on it). */
  proposalId: Schema.optionalKey(ProposalId),
  /** $label → minted id. */
  ids: Schema.optionalKey(Schema.Record(Schema.String, Schema.String)),
  /** $label → short ref, when assigned. */
  refs: Schema.optionalKey(Schema.Record(Schema.String, Schema.String)),
  /** Read tools. */
  data: Schema.optionalKey(Schema.Json),
})
export type GraphToolOutput = typeof GraphToolOutput.Type
// #endregion graph-tools

// #region repo-tool
/** Served by the runner's local MCP server "repo" (W9); never by the cloud. Always a draft PR this wave. */
export const OpenPullRequestInput = Schema.Struct({
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  body: Schema.String.check(Schema.isMaxLength(20_000)),
})
export const OpenPullRequestOutput = Schema.Struct({
  url: Schema.String,
  number: PosInt,
  branch: Schema.String,
  headSha: Schema.String,
  artifactId: Schema.NullOr(ArtifactId),
  /** The files committed by this call, also listed in the PR body (B13). */
  files: Schema.Array(Schema.String),
})
// #endregion repo-tool
